import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import type { AssistantResponseDto, UpdateAssistantDto } from '@/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle } from 'lucide-react';

const behaviorSchema = z.object({
  bufferEnabled: z.boolean().optional(),
  bufferTimeoutMs: z.number().min(0).optional(),
  messageSplitEnabled: z.boolean().optional(),
  messageSplitDelayMs: z.number().min(0).optional(),
  testAllowedSenders: z.string().optional(),
});

type BehaviorFormData = z.infer<typeof behaviorSchema>;

interface BehaviorFormProps {
  assistant?: AssistantResponseDto;
  onSubmit: (data: UpdateAssistantDto) => void;
  isSubmitting: boolean;
  error?: string;
}

export function BehaviorForm({
  assistant,
  onSubmit,
  isSubmitting,
  error,
}: BehaviorFormProps) {
  const {
    register,
    handleSubmit,
    watch,
  } = useForm<BehaviorFormData>({
    resolver: zodResolver(behaviorSchema),
    defaultValues: {
      bufferEnabled: assistant?.config?.buffer?.enabled ?? true,
      bufferTimeoutMs: assistant?.config?.buffer?.timeoutMs ?? 2000,
      messageSplitEnabled: assistant?.config?.messageSplit?.enabled ?? true,
      messageSplitDelayMs: assistant?.config?.messageSplit?.delayMs ?? 500,
      testAllowedSenders: assistant?.config?.test?.allowedSenders?.join('\n') || '',
    },
  });

  const bufferEnabled = watch('bufferEnabled');
  const messageSplitEnabled = watch('messageSplitEnabled');

  const handleFormSubmit = (data: BehaviorFormData) => {
    const config = {
      ...assistant?.config,
      buffer: {
        enabled: data.bufferEnabled,
        timeoutMs: data.bufferTimeoutMs,
      },
      messageSplit: {
        enabled: data.messageSplitEnabled,
        delayMs: data.messageSplitDelayMs,
      },
      test: {
        allowedSenders: data.testAllowedSenders
          ? data.testAllowedSenders.split('\n').filter((s) => s.trim())
          : [],
      },
    };

    const updateDto: UpdateAssistantDto = {
      config,
    };
    onSubmit(updateDto);
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Buffer Configuration */}
      <div className="space-y-4">
        <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
          Buffer de Mensagens
        </h4>

        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            id="bufferEnabled"
            {...register('bufferEnabled')}
            className="rounded"
          />
          <Label htmlFor="bufferEnabled">Habilitar buffer de mensagens</Label>
        </div>

        {bufferEnabled && (
          <div className="space-y-2 ml-6">
            <Label htmlFor="bufferTimeoutMs">Tempo de buffer (ms)</Label>
            <Input
              id="bufferTimeoutMs"
              type="number"
              {...register('bufferTimeoutMs', { valueAsNumber: true })}
              placeholder="2000"
            />
            <p className="text-sm text-muted-foreground">
              Tempo de espera para agrupar mensagens consecutivas do usuário
            </p>
          </div>
        )}
      </div>

      {/* Message Split Configuration */}
      <div className="space-y-4 pt-4 border-t">
        <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
          Divisão de Mensagens
        </h4>

        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            id="messageSplitEnabled"
            {...register('messageSplitEnabled')}
            className="rounded"
          />
          <Label htmlFor="messageSplitEnabled">Habilitar divisão de mensagens</Label>
        </div>

        {messageSplitEnabled && (
          <div className="space-y-2 ml-6">
            <Label htmlFor="messageSplitDelayMs">Delay entre mensagens (ms)</Label>
            <Input
              id="messageSplitDelayMs"
              type="number"
              {...register('messageSplitDelayMs', { valueAsNumber: true })}
              placeholder="500"
            />
            <p className="text-sm text-muted-foreground">
              Intervalo entre o envio de mensagens consecutivas da IA
            </p>
          </div>
        )}
      </div>

      {/* Test Mode Configuration */}
      {assistant?.status === 'TEST' && (
        <div className="space-y-4 pt-4 border-t">
          <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
            Modo de Teste
          </h4>

          <div className="space-y-2">
            <Label htmlFor="testAllowedSenders">Remetentes Autorizados</Label>
            <Textarea
              id="testAllowedSenders"
              {...register('testAllowedSenders')}
              placeholder="5511999999999&#10;5511888888888"
              rows={4}
            />
            <p className="text-sm text-muted-foreground">
              Um número de telefone por linha (somente esses poderão interagir no modo teste)
            </p>
          </div>
        </div>
      )}

      {/* Form Actions */}
      <div className="flex gap-3 pt-4 border-t">
        <Button type="submit" disabled={isSubmitting} className="w-full">
          {isSubmitting ? 'Salvando...' : 'Salvar Configurações'}
        </Button>
      </div>
    </form>
  );
}
